import { useFormContext } from 'react-hook-form'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../../common/integrations'
import { IntegrationSettings } from '../../schema'
import { useMemo, useState } from 'react'
import { SelectDropdown, SelectOption } from '../../../../../../components/SelectDropdown/SelectDropdown'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { IntegrationIcon } from '../../../../../../components/IntegrationIcon'
import { Button } from '../../../../../../components/Button/Button'
import { selectAccountId } from '../../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../../store/hooks'
import { useGetDescribeQuery } from '../../../../../../store/integrations/integrations'
import { FormFieldDefinition } from '../../hooks'
import { CustomObjectFieldMapping, DescriptorType, FieldDescriptor } from '../../../../../../models/Account'
import { FieldReferenceFieldsPicker } from './shared/FieldReferenceFieldsPicker'
import './AdditionalFieldSelector.scss'

interface AdditionalFieldSelectorProps {
  serviceId: IntegrationServiceId
  mappingObjectName: string
  mappingObjectIndex?: number
  ignoreKeys?: string[]
  alreadyAddedFields?: string[]
  title: string
  description: string
  fieldsShape?: 'dict' | 'array'
}

export const AdditionalFieldSelector: React.FC<AdditionalFieldSelectorProps> = ({
  serviceId,
  mappingObjectName,
  ignoreKeys,
  alreadyAddedFields,
  title,
  description,
  mappingObjectIndex,
  fieldsShape = 'dict',
}) => {
  const { watch, setValue } = useFormContext<IntegrationSettings>()
  const mappings = watch('mappings')
  const mappingValue = mappings?.[mappingObjectName as keyof typeof mappings]
  const currentMapping = mappingObjectIndex !== undefined && Array.isArray(mappingValue) ? mappingValue[mappingObjectIndex] : mappingValue
  const hasFields = currentMapping && 'fields' in currentMapping
  const hasObjectName = currentMapping && 'objectName' in currentMapping

  const dictFields = fieldsShape === 'dict' ? ((hasFields ? (currentMapping.fields as { [key: string]: FieldDescriptor[] } | undefined) : undefined) ?? {}) : {}
  const arrayFields = fieldsShape === 'array' ? ((hasFields ? (currentMapping.fields as CustomObjectFieldMapping[] | undefined) : undefined) ?? []) : []

  const currentObjectName = hasObjectName ? currentMapping.objectName : null
  const accountId = useAppSelector(selectAccountId)

  const { data: describe, isFetching: isDescribeFetching } = useGetDescribeQuery(
    {
      accountId,
      connector: serviceId,
      context: { objectName: currentObjectName! },
    },
    { skip: !currentObjectName || !accountId },
  )

  const currentObjectFormFieldDefinitions: FormFieldDefinition[] = useMemo(() => describe?.data?.describe?.formFieldDefinitions || [], [describe])
  const describeFields: Array<{ name?: string; referenceTo?: string[] }> = useMemo(() => describe?.data?.describe?.fields ?? [], [describe])
  const referenceObjectNameForKey = (key: string) => describeFields.find((f) => f.name === key)?.referenceTo?.[0] ?? null

  const [tempRows, setTempRows] = useState<string[]>([])
  const [pendingDescriptions, setPendingDescriptions] = useState<Record<string, string>>({})

  const fieldOptions = useMemo(
    () =>
      currentObjectFormFieldDefinitions
        .filter((field: FormFieldDefinition) => !(alreadyAddedFields || []).includes(field.name))
        .map((field: { name: string; label: string }): SelectOption => {
          return {
            id: field.name,
            label: `${field.label} (${field.name})`,
          }
        }) || [],
    [currentObjectFormFieldDefinitions, alreadyAddedFields],
  )

  const committedKeys =
    fieldsShape === 'array' ? arrayFields.map((f) => f.objectName) : Object.keys(dictFields).filter((key) => !(ignoreKeys || []).includes(key))

  const getArrayEntry = (key: string) => arrayFields.find((f) => f.objectName === key)

  const writeCommittedValue = (newValue: CustomObjectFieldMapping[] | { [key: string]: FieldDescriptor[] }) => {
    const updatedObject = { ...currentMapping, fields: newValue }
    const updatedMappingValue =
      mappingObjectIndex !== undefined && Array.isArray(mappingValue)
        ? mappingValue.map((item, index) => (index === mappingObjectIndex ? updatedObject : item))
        : updatedObject

    const updatedMappings = {
      ...mappings,
      [mappingObjectName]: updatedMappingValue,
    }

    setValue('mappings', updatedMappings, { shouldDirty: true, shouldValidate: true })
  }

  const handleAddField = () => {
    setTempRows((prev) => [...prev, crypto.randomUUID()])
  }

  const handleRemoveTempRow = (id: string) => {
    setTempRows((prev) => prev.filter((rowId) => rowId !== id))
    setPendingDescriptions((prev) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [id]: _removed, ...rest } = prev
      return rest
    })
  }

  const handleRemoveSavedField = (key: string) => {
    if (fieldsShape === 'array') {
      writeCommittedValue(arrayFields.filter((f) => f.objectName !== key))
      return
    }
    const newFields = { ...dictFields }
    delete newFields[key]
    writeCommittedValue(newFields)
  }

  const handleFieldSelect = (selectedOption: SelectOption, tempRowId?: string, oldKey?: string) => {
    const newKey = selectedOption.id

    if (fieldsShape === 'array') {
      const referenceObjectName = referenceObjectNameForKey(newKey)
      const existingDescription = (oldKey ? getArrayEntry(oldKey)?.description : tempRowId ? pendingDescriptions[tempRowId] : undefined) ?? ''
      const newEntry: CustomObjectFieldMapping = referenceObjectName
        ? { type: 'reference', objectName: newKey, description: existingDescription, referenceObjectName, referenceFields: [] }
        : { type: 'field', objectName: newKey, description: existingDescription }
      const updated = oldKey ? arrayFields.map((f) => (f.objectName === oldKey ? newEntry : f)) : [...arrayFields, newEntry]
      writeCommittedValue(updated)
    } else {
      const newDescriptors: FieldDescriptor[] = [{ type: DescriptorType.FIELD, value: newKey }]
      const updatedFields = { ...dictFields }
      if (oldKey && oldKey !== newKey) {
        delete updatedFields[oldKey]
      }
      updatedFields[newKey] = newDescriptors
      writeCommittedValue(updatedFields)
    }

    if (tempRowId) {
      handleRemoveTempRow(tempRowId)
    }
  }

  const handleDescriptionChange = (key: string, value: string) => {
    writeCommittedValue(arrayFields.map((f) => (f.objectName === key ? { ...f, description: value } : f)))
  }

  const handleReferenceFieldsChange = (key: string, referenceFields: string[]) => {
    writeCommittedValue(arrayFields.map((f) => (f.objectName === key && f.type === 'reference' ? { ...f, referenceFields } : f)))
  }

  return (
    <Container className='p-0'>
      <Row className='mb-3'>
        <Col>
          <h4 className='mb-2'>{title}</h4>
          <p className='zocks-text-gray-600'>{description}</p>
        </Col>
      </Row>
      <Row className='mb-2 fw-bold'>
        <Col>
          <div className={`field-selector-grid${fieldsShape === 'array' ? ' has-description' : ''}`}>
            <div className='d-flex align-items-center'>
              <IntegrationIcon serviceId={serviceId} width='1rem' height='1rem' fill='black' />
              <span className='ms-2'>{getIntegrationDisplayName(serviceId).toUpperCase()} FIELD</span>
            </div>
            {fieldsShape === 'array' && (
              <div>
                <span>Description</span> <span className='zocks-text-gray-600'>(max 150 characters)</span>
              </div>
            )}
            <div />
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className='d-flex flex-column gap-2 mb-3'>
            {committedKeys.map((key: string) => {
              const currentFieldFormFieldDefinition = currentObjectFormFieldDefinitions.find((field) => field.name === key) || { name: key, label: key }
              const currentFieldOptions = [
                ...fieldOptions,
                {
                  id: currentFieldFormFieldDefinition.name,
                  label: `${currentFieldFormFieldDefinition.label} (${currentFieldFormFieldDefinition.name})`,
                },
              ]
              const currentOption = currentFieldOptions.find((opt: SelectOption) => opt.id === key) || { id: key, label: key }
              const arrayEntry = fieldsShape === 'array' ? getArrayEntry(key) : undefined

              return (
                <div key={key}>
                  <div className={`field-selector-grid${fieldsShape === 'array' ? ' has-description' : ''}`}>
                    <SelectDropdown
                      options={currentFieldOptions}
                      initialSelectedOption={currentOption}
                      onOptionSelected={(selected) => handleFieldSelect(selected, undefined, key)}
                      placeholder='Select field'
                      searchable
                      loading={isDescribeFetching}
                    />
                    {fieldsShape === 'array' && (
                      <Form.Control
                        type='text'
                        placeholder='Add description'
                        maxLength={150}
                        value={arrayEntry?.description ?? ''}
                        onChange={(e) => handleDescriptionChange(key, e.target.value)}
                      />
                    )}
                    <Button type='button' variant='primaryGhost' className='p-2' onClick={() => handleRemoveSavedField(key)} title='Remove field'>
                      <i className='bi bi-trash' />
                    </Button>
                  </div>
                  {arrayEntry?.type === 'reference' && (
                    <div className='mt-1'>
                      <FieldReferenceFieldsPicker
                        serviceId={serviceId}
                        referenceObjectName={arrayEntry.referenceObjectName}
                        referenceFields={arrayEntry.referenceFields ?? []}
                        onChange={(referenceFields) => handleReferenceFieldsChange(key, referenceFields)}
                      />
                    </div>
                  )}
                </div>
              )
            })}

            {tempRows.map((tempId) => (
              <div key={tempId} className={`field-selector-grid${fieldsShape === 'array' ? ' has-description' : ''}`}>
                <SelectDropdown
                  searchable
                  options={fieldOptions}
                  onOptionSelected={(selected) => handleFieldSelect(selected, tempId)}
                  placeholder='Select field'
                  initialSelectedOption={null}
                  loading={isDescribeFetching}
                />
                {fieldsShape === 'array' && (
                  <Form.Control
                    type='text'
                    placeholder='Add description'
                    maxLength={150}
                    value={pendingDescriptions[tempId] ?? ''}
                    onChange={(e) => setPendingDescriptions((prev) => ({ ...prev, [tempId]: e.target.value }))}
                  />
                )}
                <Button type='button' variant='primaryGhost' className='p-2' onClick={() => handleRemoveTempRow(tempId)} title='Remove row'>
                  <i className='bi bi-trash' />
                </Button>
              </div>
            ))}
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <Button type='button' variant='primaryGhost' className='border-0' onClick={handleAddField}>
            + Add field
          </Button>
        </Col>
      </Row>
    </Container>
  )
}
